import { forwardRef, useEffect, useImperativeHandle, useState, Ref, useCallback } from 'react';
import Avatar from '../avatar';
import { AVATAR_BORDER } from '../../global/constants';

// Define the interface for mention items
interface MentionItem {
  id: string;
  name: string;
  email: string;
  profile_photo?: string;
}

// Define the interface for props
interface MentionListProps {
  items: MentionItem[];
  command: (item: { id: string; label: string; email: string }) => void;
}

// Define the type for the ref's imperative handle
export interface MentionListHandle {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListHandle, MentionListProps>((props, ref: Ref<MentionListHandle>) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { items, command } = props;

  const selectItem = useCallback(
    (index: number) => {
      const item = items?.[index];
      if (item) {
        command({ id: item.id, label: item.name, email: item.email });
      }
    },
    [items, command]
  );

  const upHandler = useCallback(() => {
    if (!props.items || props.items.length === 0) return;
    setSelectedIndex((prev) => (prev + props.items.length - 1) % props.items.length);
  }, [props.items]);

  const downHandler = useCallback(() => {
    if (!props.items || props.items.length === 0) return;
    setSelectedIndex((prev) => (prev + 1) % props.items.length);
  }, [props.items]);

  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectItem, selectedIndex]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useEffect(() => {
    if (props.items?.length > 0) {
      const preventDefault = (e: Event) => {
        const targetElement = e.target as HTMLElement;
        if (targetElement.closest('.menu-button')) {
          return;
        }
        e.preventDefault();
      };

      document.addEventListener('wheel', preventDefault, { passive: false });
      document.addEventListener('touchmove', preventDefault, { passive: false });

      return () => {
        document.removeEventListener('wheel', preventDefault);
        document.removeEventListener('touchmove', preventDefault);
      };
    }
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    }
  }));

  return props.items?.length > 0 ? (
    <div className='dropdown-menu mention-list'>
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            className={index === selectedIndex ? 'is-selected' : 'menu-button'}
            key={item?.name}
            onClick={() => selectItem(index)}>
            <Avatar
              imgSrc={item?.profile_photo || ''}
              name={item?.name ? item?.name : 'U N'}
              size={20}
              borderVariant={AVATAR_BORDER.NONE}
            />
            {item?.name}
          </button>
        ))
      ) : (
        <div className='item'>No result</div>
      )}
    </div>
  ) : (
    <></>
  );
});

export default MentionList;
